import React, { useReducer, Dispatch, Reducer, useContext } from "react"

import { ProductGroup } from "typings/products"
import { DrugCategoryEnum, DrugTypeEnum, GetDisplayedProductsDisplayedProducts } from "typings/types"
import products from "shared/products/products.json"
import { transform } from "shared/products/transformer"
import isEqual from "lodash.isequal"

const preprocessProducts = (initialProducts: ProductGroup[]) =>
  initialProducts.map(p => {
    if (p.drugType === DrugTypeEnum.MigraineRelief) {
      return {
        ...p,
        drugCategory: DrugCategoryEnum.Acute
      }
    }

    return { ...p }
  })

export enum ProductActionEventType {
  updateProductGroups = "updateProductGroups"
}

interface UpdateProductGroups {
  event: typeof ProductActionEventType.updateProductGroups
  products: GetDisplayedProductsDisplayedProducts[]
}

export type AnyAction = UpdateProductGroups

interface InitialState {
  productGroups: ProductGroup[]
}

const initialState: InitialState = {
  productGroups: preprocessProducts((products as unknown) as ProductGroup[])
}

const ProductsStateContext = React.createContext<InitialState>(initialState)
const ProductsDispatchContext = React.createContext<{ dispatch: Dispatch<AnyAction> }>({
  dispatch: () => null
})

const reducer: React.Reducer<InitialState, AnyAction> = (state, action): InitialState => {
  switch (action.event) {
    case ProductActionEventType.updateProductGroups:
      const productsToDispatch = preprocessProducts(transform({ displayedProducts: action.products }))
      if (isEqual(state.productGroups, productsToDispatch)) {
        return state
      }

      return {
        productGroups: productsToDispatch
      }
    default:
      state = state || initialState

      return state
  }
}

const useProductsState = () => {
  const context = useContext(ProductsStateContext)
  if (context === undefined) throw new Error("useProductsState must be used within a ProductsProvider")
  return context
}

const useProductsDispatch = () => {
  const context = useContext(ProductsDispatchContext)
  if (context === undefined) throw new Error("useProductsDispatch must be used within a ProductsProvider")
  return context
}

const ProductsProvider: React.FunctionComponent = ({ children }: { children?: React.ReactNode }) => {
  const [state, dispatch] = useReducer<Reducer<InitialState, AnyAction>>(reducer, initialState)

  return (
    <ProductsStateContext.Provider value={{ ...state }}>
      <ProductsDispatchContext.Provider value={{ dispatch }}>{children}</ProductsDispatchContext.Provider>
    </ProductsStateContext.Provider>
  )
}

export { ProductsProvider, useProductsState, useProductsDispatch }
